import { getErrorMessage } from "@arti/utilities";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";
import {
  type AttachRepositoryInput,
  type SourceControlRepositoryOption,
  useSourceControlCatalog,
} from "../useChannelWorkspaceRepositories";
import { ModalErrorBlock } from "./ConfirmModal";

interface RepositoryDraft {
  input: AttachRepositoryInput;
  remoteSlug: string;
  status: "pending" | "attaching" | "attached" | "failed";
  error: string | null;
}

export async function attachRepositoriesInOrder(
  inputs: readonly AttachRepositoryInput[],
  attach: (input: AttachRepositoryInput) => Promise<void>,
  onAttached: (input: AttachRepositoryInput) => void,
) {
  for (const input of inputs) {
    await attach(input);
    onAttached(input);
  }
}

export function AttachRepositoryBatchModal({
  attachedRepoKeys,
  onAttach,
  onClose,
  onSingle,
}: {
  attachedRepoKeys: ReadonlySet<string>;
  onAttach: (input: AttachRepositoryInput) => Promise<void>;
  onClose: () => void;
  onSingle: () => void;
}) {
  const catalog = useSourceControlCatalog(true);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [repositories, setRepositories] = useState<
    SourceControlRepositoryOption[]
  >([]);
  const [drafts, setDrafts] = useState<RepositoryDraft[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const { loadRepositories } = catalog;

  useEffect(() => {
    if (!providerId && catalog.providers.length > 0) {
      setProviderId(catalog.providers[0].providerId);
    }
  }, [catalog.providers, providerId]);

  useEffect(() => {
    if (!providerId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRepositories([]);
    void loadRepositories(providerId)
      .then((items) => {
        if (!cancelled) {
          setRepositories(items);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setError(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadRepositories, providerId]);

  const pending = drafts.filter((draft) => draft.status !== "attached");
  const completed = drafts.length - pending.length;
  const update = (
    input: AttachRepositoryInput,
    change: Partial<RepositoryDraft>,
  ) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.input.providerId === input.providerId &&
        draft.input.repositoryId === input.repositoryId
          ? { ...draft, ...change }
          : draft,
      ),
    );
  };
  const submit = async () => {
    if (submitting.current || pending.length === 0) {
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await attachRepositoriesInOrder(
        pending.map((draft) => draft.input),
        async (input) => {
          update(input, { error: null, status: "attaching" });
          try {
            await onAttach(input);
          } catch (error) {
            update(input, { error: getErrorMessage(error), status: "failed" });
            throw error;
          }
        },
        (input) => update(input, { error: null, status: "attached" }),
      );
    } catch {
      setError(
        "Stopped at the failed repository. Successful attachments are kept; retry only submits the remaining repositories.",
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Attach Repositories"
      subtitle="Select repositories, then configure each branch and permission before attaching."
      widthClass="max-w-3xl"
      onClose={() => {
        if (!submitting.current) {
          onClose();
        }
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={onClose}
          >
            {completed > 0 ? "Done" : "Cancel"}
          </Button>
          <Button
            size="sm"
            disabled={busy || pending.length === 0}
            loading={busy}
            loadingText="Attaching repositories…"
            onClick={() => void submit()}
          >
            Attach {pending.length}{" "}
            {pending.length === 1 ? "repository" : "repositories"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-3">
        {(errorMessage ?? catalog.error) && (
          <ModalErrorBlock message={errorMessage ?? catalog.error ?? ""} />
        )}
        {completed > 0 && (
          <p
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            {completed} attached. Cloning may still be pending. If the workspace
            asks for a restart, restart once after finishing all attachments.
          </p>
        )}
        <fieldset disabled={busy} className="grid gap-3 disabled:opacity-60">
          <label className="grid gap-1 text-xs">
            Provider
            <select
              value={providerId ?? ""}
              onChange={(event) => setProviderId(event.currentTarget.value)}
              className="border border-slate-300 bg-transparent p-2 dark:border-slate-700"
            >
              {catalog.providers.map((provider) => (
                <option key={provider.providerId} value={provider.providerId}>
                  {provider.displayName}
                </option>
              ))}
            </select>
          </label>
          <input
            aria-label="Search repositories"
            placeholder="Search repositories…"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            className="border border-slate-300 bg-transparent p-2 text-sm dark:border-slate-700"
          />
          <div
            className="grid max-h-48 gap-1 overflow-y-auto"
            aria-label="Repositories"
          >
            {loading || catalog.loading ? (
              <p className="text-xs">Loading repositories…</p>
            ) : (
              repositories
                .filter((repository) =>
                  repository.remoteSlug
                    .toLowerCase()
                    .includes(search.toLowerCase()),
                )
                .map((repository) => {
                  const selected = drafts.some(
                    (draft) =>
                      draft.input.providerId === providerId &&
                      draft.input.repositoryId === repository.repositoryId,
                  );
                  const attached = attachedRepoKeys.has(
                    `${providerId}:${repository.repositoryId}`,
                  );
                  return (
                    <label
                      key={repository.repositoryId}
                      className="flex items-center gap-2 p-1 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={attached}
                        onChange={(event) => {
                          if (!providerId) {
                            return;
                          }
                          if (event.currentTarget.checked) {
                            setDrafts((current) => [
                              ...current,
                              {
                                error: null,
                                input: {
                                  branchSelection: {
                                    branch: repository.defaultBranch,
                                    mode: "existing",
                                  },
                                  permission: "read",
                                  providerId,
                                  repositoryId: repository.repositoryId,
                                },
                                remoteSlug: repository.remoteSlug,
                                status: "pending",
                              },
                            ]);
                          } else {
                            setDrafts((current) =>
                              current.filter(
                                (draft) =>
                                  !(
                                    draft.input.providerId === providerId &&
                                    draft.input.repositoryId ===
                                      repository.repositoryId
                                  ),
                              ),
                            );
                          }
                        }}
                      />
                      {repository.remoteSlug}
                      {attached ? " · attached" : ""}
                    </label>
                  );
                })
            )}
          </div>
          {drafts.map((draft) => (
            <div
              key={`${draft.input.providerId}:${draft.input.repositoryId}`}
              className="grid gap-2 border border-slate-300 p-3 dark:border-slate-700"
            >
              <div className="flex items-center justify-between gap-2 text-xs">
                <strong>{draft.remoteSlug}</strong>
                <span>{draft.status}</span>
              </div>
              {draft.error && <ModalErrorBlock message={draft.error} />}
              <RepositoryDraftFields
                input={draft.input}
                disabled={draft.status === "attached"}
                onChange={(input) => update(draft.input, { input })}
              />
              {draft.status !== "attached" && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setDrafts((current) =>
                      current.filter((candidate) => candidate !== draft),
                    )
                  }
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          {drafts.length === 0 && (
            <Button variant="secondary" size="sm" onClick={onSingle}>
              Create a branch or discover branches for one repository
            </Button>
          )}
        </fieldset>
      </div>
    </Modal>
  );
}

function RepositoryDraftFields({
  input,
  disabled,
  onChange,
}: {
  input: AttachRepositoryInput;
  disabled: boolean;
  onChange: (input: AttachRepositoryInput) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="grid gap-1 text-xs">
        Branch
        <input
          disabled={disabled}
          value={
            input.branchSelection?.mode === "existing"
              ? (input.branchSelection.branch ?? "")
              : ""
          }
          placeholder="Default branch"
          onChange={(event) =>
            onChange({
              ...input,
              branchSelection: {
                branch: event.currentTarget.value.trim() || null,
                mode: "existing",
              },
            })
          }
          className="border border-slate-300 bg-transparent p-2 dark:border-slate-700"
        />
      </label>
      <label className="grid gap-1 text-xs">
        Permission
        <select
          disabled={disabled}
          value={input.permission}
          onChange={(event) =>
            onChange({
              ...input,
              permission:
                event.currentTarget.value === "write" ? "write" : "read",
            })
          }
          className="border border-slate-300 bg-transparent p-2 dark:border-slate-700"
        >
          <option value="read">Read</option>
          <option value="write">Write</option>
        </select>
      </label>
    </div>
  );
}
